import React, { useState, useEffect } from 'react';
import { User } from '../types';

interface DetailSettingsScreenProps {
  currentUser: User;
  onBack: () => void;
}

type CallAcceptanceSetting = 'everyone' | 'favorites_only' | 'none';

const DetailSettingsScreen: React.FC<DetailSettingsScreenProps> = ({
  currentUser,
  onBack
}) => {
  const [callAcceptance, setCallAcceptance] = useState<CallAcceptanceSetting>('everyone');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const API_URL = 'https://voicechatapi.kasaneko.com';

  useEffect(() => {
    loadSettings();
  }, [currentUser.userId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/users/settings/${currentUser.userId}`);
      const data = await response.json();
      
      if (data.success && data.settings) {
        setCallAcceptance(data.settings.callAcceptance || 'everyone');
      }
    } catch (error) {
      console.error('設定取得エラー:', error);
      // エラー時はデフォルト値を使用
      setCallAcceptance('everyone');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const response = await fetch(`${API_URL}/api/users/settings/${currentUser.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callAcceptance
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ 設定を保存しました！');
      } else {
        alert(data.error || '設定の保存に失敗しました');
      }
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert('設定の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#333',
        fontSize: '18px'
      }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: isMobile ? '10px' : '20px',
      fontFamily: 'Arial, sans-serif',
      color: '#333',
      overflowY: 'auto'
    }}>
      {/* ヘッダー */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isMobile ? '15px' : '30px'
      }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px' }}>
          詳細設定
        </h1>
        <button
          onClick={onBack}
          style={{
            background: '#667eea',
            color: 'white',
            border: 'none',
            padding: isMobile ? '8px 12px' : '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: 'bold'
          }}
        >
          ← 戻る
        </button>
      </div>

      {/* メインコンテンツ */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: isMobile ? '20px' : '40px',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        {/* 通話承諾設定 */}
        <div style={{ marginBottom: isMobile ? '20px' : '40px' }}>
          <h2 style={{
            fontSize: isMobile ? '16px' : '20px',
            fontWeight: 'bold',
            marginBottom: isMobile ? '12px' : '20px',
            borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
            paddingBottom: '10px',
            color: '#333'
          }}>
            通話承諾について
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '15px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: isMobile ? '10px' : '15px',
              background: callAcceptance === 'everyone' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'background 0.3s',
              border: `2px solid ${callAcceptance === 'everyone' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`
            }}>
              <input
                type="radio"
                name="callAcceptance"
                value="everyone"
                checked={callAcceptance === 'everyone'}
                onChange={(e) => setCallAcceptance(e.target.value as CallAcceptanceSetting)}
                style={{ marginRight: isMobile ? '10px' : '15px', width: isMobile ? '16px' : '20px', height: isMobile ? '16px' : '20px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: isMobile ? '13px' : '16px', color: '#333' }}>1: 誰からでもOK</div>
                <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#666' }}>すべてのユーザーからの通話依頼を受け取ります</div>
              </div>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: isMobile ? '10px' : '15px',
              background: callAcceptance === 'favorites_only' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'background 0.3s',
              border: `2px solid ${callAcceptance === 'favorites_only' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`
            }}>
              <input
                type="radio"
                name="callAcceptance"
                value="favorites_only"
                checked={callAcceptance === 'favorites_only'}
                onChange={(e) => setCallAcceptance(e.target.value as CallAcceptanceSetting)}
                style={{ marginRight: isMobile ? '10px' : '15px', width: isMobile ? '16px' : '20px', height: isMobile ? '16px' : '20px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: isMobile ? '13px' : '16px', color: '#333' }}>2: 自分がお気に入りしている相手からのみ受け取る</div>
                <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#666' }}>お気に入りユーザーからの通話依頼のみ受け取ります</div>
              </div>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: isMobile ? '10px' : '15px',
              background: callAcceptance === 'none' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'background 0.3s',
              border: `2px solid ${callAcceptance === 'none' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`
            }}>
              <input
                type="radio"
                name="callAcceptance"
                value="none"
                checked={callAcceptance === 'none'}
                onChange={(e) => setCallAcceptance(e.target.value as CallAcceptanceSetting)}
                style={{ marginRight: isMobile ? '10px' : '15px', width: isMobile ? '16px' : '20px', height: isMobile ? '16px' : '20px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: isMobile ? '13px' : '16px', color: '#333' }}>3: 通話申請を受け入れない</div>
                <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#666' }}>すべての通話依頼を拒否します</div>
              </div>
            </label>
          </div>
        </div>

        {/* 保存ボタン */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: isMobile ? '12px' : '15px',
            background: saving ? '#ccc' : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
          }}
        >
          {saving ? '保存中...' : '💾 保存する'}
        </button>
      </div>
    </div>
  );
};

export default DetailSettingsScreen;

